import { useState, useEffect } from "react";
import { intakeService, tasksService, auditLogService } from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Upload, X, FileText, Image as ImageIcon, Loader2, ChevronDown, Bold, Italic, List, ListOrdered, Heading2 } from "lucide-react";

const departmentRequestTypes = {
  "Social Media Ambassadors": [
    "Content Creation Request",
    "Social Media Campaign",
    "Post Design Request",
    "Analytics Report",
    "Account Management"
  ],
  "HR": [
    "Recruitment Request",
    "Training Request",
    "Leave Request",
    "Policy Update",
    "Employee Benefits",
    "Performance Review"
  ],
  "Email Marketing": [
    "Email Campaign",
    "Newsletter Setup",
    "Automation Workflow",
    "Template Design",
    "List Management"
  ],
  "Client Relations": [
    "Client Onboarding",
    "Client Meeting",
    "Feedback Collection",
    "Account Update",
    "Complaint Resolution"
  ],
  "Creativity": [
    "Design Request",
    "Brand Asset Creation",
    "Illustration Request",
    "Video Production",
    "Motion Graphics"
  ],
  "Content Creation": [
    "Blog Post",
    "Article Writing",
    "Copywriting",
    "Content Strategy",
    "Research Request"
  ],
  "Public Relations": [
    "Press Release",
    "Media Contact",
    "Event Coordination",
    "PR Campaign",
    "Crisis Management",
    "SEO Content Writing"
  ],
  "Digital Marketing": [
    "Ad Campaign",
    "SEO Request",
    "Website Update",
    "Analytics Dashboard",
    "Marketing Strategy"
  ],
  "Operations": [
    "Process Improvement",
    "Resource Request",
    "Vendor Coordination",
    "Logistics Request",
    "Documentation"
  ],
  "Finance": [
    "Invoice Request",
    "Budget Approval",
    "Expense Report",
    "Financial Report",
    "Payment Processing"
  ],
  "IT": [
    "Technical Support",
    "System Access",
    "Software Request",
    "Security Issue",
    "Infrastructure"
  ],
  "Legal": [
    "Contract Review",
    "Legal Consultation",
    "Compliance Request",
    "Document Preparation",
    "Risk Assessment"
  ],
  "Sales": [
    "Lead Generation",
    "Sales Report",
    "Client Presentation",
    "Pricing Quote",
    "Territory Assignment"
  ],
  "Customer Service": [
    "Support Ticket",
    "Service Request",
    "Customer Feedback",
    "Warranty Claim",
    "Refund Request"
  ]
};

const NewIntakeForm = ({ open, onOpenChange, users = [] }) => {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    requestType: "",
    clientSubsidiary: "",
    requestingDepartment: "",
    targetDepartment: "",
    assignedOwner: "",
    businessImpact: "",
    revenueImpact: "",
    deadline: null,
    description: "",
    attachments: []
  });

  const departments = [
    "Social Media Ambassadors",
    "HR",
    "Email Marketing",
    "Client Relations",
    "Creativity",
    "Content Creation",
    "Public Relations",
    "Digital Marketing"
  ];

  const targetDepartments = [
    "Social Media Ambassadors",
    "HR",
    "Email Marketing",
    "Client Relations",
    "Creativity",
    "Content Creation",
    "Public Relations",
    "Digital Marketing"
  ];

  const priorityLevels = ["Low", "Medium", "High", "Critical"];

  const usersByDepartment = users.reduce((acc, user) => {
    const dept = user.department || "Other";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(user);
    return acc;
  }, {});

  useEffect(() => {
    if (!formData.targetDepartment) {
      setShowOwnerDropdown(false);
      setFormData(prev => ({ ...prev, assignedOwner: "" }));
    }
  }, [formData.targetDepartment]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      name: file.name,
      type: file.type.startsWith("image/") ? "image" : "document",
      url: URL.createObjectURL(file),
      file
    }));
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments]
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData(prev => ({ ...prev, description: value }));
  };

  const insertFormatting = (format) => {
    const textarea = document.getElementById("description");
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.description;
    const selectedText = text.substring(start, end);
    
    let newText = "";
    switch (format) {
      case "bold":
        newText = `**${selectedText}**`;
        break;
      case "italic":
        newText = `*${selectedText}*`;
        break;
      case "heading":
        newText = `\n## ${selectedText}`;
        break;
      case "bullet":
        newText = `\n- ${selectedText}`;
        break;
      case "numbered":
        newText = `\n1. ${selectedText}`;
        break;
      default:
        newText = selectedText;
    }
    
    const updatedText = text.substring(0, start) + newText + text.substring(end);
    setFormData(prev => ({ ...prev, description: updatedText }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.targetDepartment || !formData.requestType) {
      alert("Please fill in required fields");
      return;
    }

    setLoading(true);
    try {
      const { attachments, ...dataWithoutAttachments } = formData;
      const attachmentMeta = attachments.map(att => ({
        name: att.name,
        type: att.type
      }));
      
      const intake = await intakeService.create({
        ...dataWithoutAttachments,
        attachments: attachmentMeta,
        status: "awaiting",
        createdAt: new Date()
      });
      
      await auditLogService.logIntakeCreated(
        intake.id,
        dataWithoutAttachments.title,
        userData?.id,
        userData?.name || "Unknown User"
      );
      
      if (formData.assignedOwner) {
        await intakeService.update(intake.id, {
          notificationSent: true,
          notifiedAt: new Date()
        });
        
        await tasksService.create({
          title: dataWithoutAttachments.title,
          description: dataWithoutAttachments.description,
          assignedTo: dataWithoutAttachments.assignedOwner,
          targetDepartment: dataWithoutAttachments.targetDepartment,
          requestType: dataWithoutAttachments.requestType,
          requestingDepartment: dataWithoutAttachments.requestingDepartment,
          clientSubsidiary: dataWithoutAttachments.clientSubsidiary,
          businessImpact: dataWithoutAttachments.businessImpact,
          revenueImpact: dataWithoutAttachments.revenueImpact,
          dueDate: dataWithoutAttachments.deadline,
          deadline: dataWithoutAttachments.deadline,
          attachments: attachmentMeta,
          status: "Pending Approval",
          priority: dataWithoutAttachments.businessImpact || "Medium",
          isIntakeTask: true,
          intakeId: intake.id,
          createdAt: new Date()
        });
      }
      
      setFormData({
        title: "",
        requestType: "",
        clientSubsidiary: "",
        requestingDepartment: "",
        targetDepartment: "",
        assignedOwner: "",
        businessImpact: "",
        revenueImpact: "",
        deadline: null,
        description: "",
        attachments: []
      });
      setShowOwnerDropdown(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Error creating intake:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      requestType: "",
      clientSubsidiary: "",
      requestingDepartment: "",
      targetDepartment: "",
      assignedOwner: "",
      businessImpact: "",
      revenueImpact: "",
      deadline: null,
      description: "",
      attachments: []
    });
    setShowOwnerDropdown(false);
    onOpenChange(false);
  };

  const availableRequestTypes = departmentRequestTypes[formData.targetDepartment] || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">New Intake</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title <span className="text-red-500">*</span></Label>
            <Input
              id="title"
              placeholder="Enter intake title"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetDepartment">Target Department <span className="text-red-500">*</span></Label>
            <Select 
              value={formData.targetDepartment} 
              onValueChange={(value) => {
                handleInputChange("targetDepartment", value);
                handleInputChange("requestType", "");
                setShowOwnerDropdown(true);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select target department" />
              </SelectTrigger>
              <SelectContent>
                {targetDepartments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showOwnerDropdown && (
            <div className="space-y-2">
              <Label htmlFor="assignedOwner">Assigned Owner</Label>
              <Select 
                value={formData.assignedOwner} 
                onValueChange={(value) => handleInputChange("assignedOwner", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {(usersByDepartment[formData.targetDepartment] || []).map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="requestType">Request Type <span className="text-red-500">*</span></Label>
            <Select 
              value={formData.requestType} 
              onValueChange={(value) => handleInputChange("requestType", value)}
              disabled={!formData.targetDepartment}
            >
              <SelectTrigger>
                <SelectValue placeholder={formData.targetDepartment ? "Select request type" : "Select target department first"} />
              </SelectTrigger>
              <SelectContent>
                {availableRequestTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="requestingDepartment">Requesting Department <span className="text-red-500">*</span></Label>
            <Select 
              value={formData.requestingDepartment} 
              onValueChange={(value) => {
                handleInputChange("requestingDepartment", value);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientSubsidiary">Client Subsidiary</Label>
            <Input
              id="clientSubsidiary"
              placeholder="Enter client subsidiary"
              value={formData.clientSubsidiary}
              onChange={(e) => handleInputChange("clientSubsidiary", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="businessImpact">Business Impact</Label>
            <Select 
              value={formData.businessImpact} 
              onValueChange={(value) => handleInputChange("businessImpact", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select impact" />
              </SelectTrigger>
              <SelectContent>
                {priorityLevels.map(level => (
                  <SelectItem key={level} value={level}>{level}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="revenueImpact">Revenue Impact</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <Input
                id="revenueImpact"
                type="number"
                placeholder="Enter amount"
                value={formData.revenueImpact}
                onChange={(e) => handleInputChange("revenueImpact", e.target.value)}
                className="pl-7"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Deadline</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <ChevronDown className="w-4 h-4 mr-2" />
                  {formData.deadline ? formData.deadline.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "Select deadline"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.deadline}
                  onSelect={(date) => handleInputChange("deadline", date)}
                  className="rounded-md"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Full Description</Label>
            <div className="border rounded-md overflow-hidden">
              <div className="flex items-center gap-1 bg-slate-50 p-2 border-b">
                <button
                  type="button"
                  onClick={() => insertFormatting("bold")}
                  className="p-1.5 hover:bg-slate-200 rounded"
                  title="Bold"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting("italic")}
                  className="p-1.5 hover:bg-slate-200 rounded"
                  title="Italic"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting("heading")}
                  className="p-1.5 hover:bg-slate-200 rounded"
                  title="Heading"
                >
                  <Heading2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting("bullet")}
                  className="p-1.5 hover:bg-slate-200 rounded"
                  title="Bullet List"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting("numbered")}
                  className="p-1.5 hover:bg-slate-200 rounded"
                  title="Numbered List"
                >
                  <ListOrdered className="w-4 h-4" />
                </button>
              </div>
              <Textarea
                id="description"
                placeholder="Enter detailed description... (Use formatting buttons above)"
                value={formData.description}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                className="h-[160px] resize-none border-0 focus:ring-0"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Attachments (Image or Document)</Label>
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
              <label className="flex flex-col items-center cursor-pointer">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Click to upload or drag and drop</span>
                <span className="text-xs text-gray-400">SVG, PNG, JPG, PDF, DOCX</span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileUpload}
                />
              </label>
            </div>
            {formData.attachments.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                {formData.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded border">
                    <div className="flex items-center gap-2 truncate">
                      {attachment.type === "image" ? (
                        <ImageIcon className="w-4 h-4 text-blue-500" />
                      ) : (
                        <FileText className="w-4 h-4 text-gray-500" />
                      )}
                      <span className="text-sm truncate">{attachment.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            className="border-gray-300"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            variant="default"
            className="bg-navy hover:bg-navy-light"
          >
            {loading ? (
              <span style={{ color: "white" }}>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </span>
            ) : (
              <span style={{ color: "white" }}>Create Intake</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewIntakeForm;
